"use client";

import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { Flex, FlexColumnAlignJustifyCenter } from "@/components/Flex";
import MultiInputWithMenu from "@/components/MultiInputWithMenu";
import { UNUSUAL_TEXT_KEY } from "@/components/unusual/constants";
import { borderRadius } from "@/utils/borderRadius";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { RawFRQ } from "@knowt/syncing/graphql/customSchema";
import { AP_EXAM_FRQ_MAP, AP_EXAM_MCQ_MAP } from "@knowt/syncing/hooks/exams/constants";
import { examNameToUrlComponent } from "@knowt/syncing/hooks/exams/utils";
import { subjectNameToUrlComponent } from "@knowt/syncing/hooks/subjects/utils";
import { toast } from "react-hot-toast";
import SectionContainer from "../../components/SectionContainer";
import styles from "../uploadLandingPages.module.css";
import SelectInput from "./SelectInput";
import LandingPagesSwitcher from "../../components/LandingPagesSwitcher";
import Image from "next/image";
import br from "@/styles/breakpoints.module.css";
import { ASSETS_URL } from "@/config/deployConstants";
import { useRouter } from "@bprogress/next";

const practiceRoom = `${ASSETS_URL}/images/practice-room.png`;

export const MultiSelectInput = ({
    options,
    selected,
    onChange,
    placeholder,
    rootStyle,
}: {
    options: { value: string; label: string }[];
    selected: string[];
    onChange: (v: string[], selectedV: string) => void;
    placeholder: string;
    rootStyle?: React.CSSProperties;
}) => {
    return (
        <MultiInputWithMenu
            options={options}
            values={selected.map(v => ({ value: v, label: v }))}
            renderLabel={({ values }) => {
                return values.length === 1 ? values[0]?.label : `${values.length} selected`;
            }}
            onChange={(newValue, selectedValue) => {
                onChange(
                    newValue.map(v => v.value),
                    selectedValue.value
                );
            }}
            inputProps={{
                className: "secondaryText1",
                placeholder,
                sx: {
                    height: "4rem",
                    borderRadius: 999,
                    color: themeColors.neutralBlack,
                    width: "100%",
                    padding: `${spacing.XS_2} ${spacing.SM}`,
                    backgroundColor: "transparent",
                },
            }}
            ulProps={{ sx: { borderRadius: "1.2rem", top: "0", boxShadow: "0 6px 20px 0 rgba(0,0,0,.08)" } }}
            rootProps={{
                style: {
                    border: `1px solid ${themeColors.neutral1}`,
                    borderRadius: 999,
                    maxWidth: "34.4rem",
                    width: "100%",
                    ...rootStyle,
                },
            }}
        />
    );
};

type ExamSelectorSectionProps = {
    sectionTitle: string;
    sectionSubTitle: string;
    type?: string;
    exam?: string | null;
    isResponseRoom?: boolean;
    examFRQs?: RawFRQ[];
    unusualKey?: UNUSUAL_TEXT_KEY;
    unusualAdditional?: string;
};

// Swap with only the AP exams supported at launch
const MCQ_EXAMS = Object.keys(AP_EXAM_MCQ_MAP);
const FRQ_EXAMS = Object.keys(AP_EXAM_FRQ_MAP);

const ExamSelectorSection = ({
    sectionTitle,
    sectionSubTitle,
    type = "AP",
    exam = null,
    isResponseRoom = false,
    unusualKey,
    unusualAdditional,
}: ExamSelectorSectionProps) => {
    const router = useRouter();
    const exams = isResponseRoom ? FRQ_EXAMS : MCQ_EXAMS;

    const handleSelectSubject = (newSubject: string) => {
        router.replace(
            `/exams/${examNameToUrlComponent(type)}/${subjectNameToUrlComponent(newSubject)}/${
                isResponseRoom ? "frq-room" : "practice-test-room"
            }`
        );
    };

    const handleStartStudy = () => {
        if (!exam) {
            return toast.error("Please select an exam first.");
        }

        if (isResponseRoom) {
            router.push(`/exams/AP/${examNameToUrlComponent(exam)}/frq`);
        } else {
            router.push(`/exams/AP/${examNameToUrlComponent(exam)}/mcq`);
        }
    };

    return (
        <>
            <LandingPagesSwitcher
                pages={[
                    {
                        name: "Practice Test Room",
                        route: exam
                            ? `/exams/AP/${examNameToUrlComponent(exam)}/practice-test-room`
                            : `/exams/AP/practice-test-room`,
                        alternateRoutes: [
                            "/exams/AP/*/practice-test-room",
                            "/exams/AP/*/*/practice-test-room",
                            "/exams/AP/*/*/*/practice-test-room",
                        ],
                    },
                    {
                        name: "Free Response Room",
                        route: exam ? `/exams/AP/${examNameToUrlComponent(exam)}/frq-room` : `/exams/AP/frq-room`,
                        alternateRoutes: ["/exams/AP/*/frq-room", "/exams/AP/*/*/frq-room", "/exams/AP/*/*/*/frq-room"],
                    },
                    {
                        name: "AP Score Calculator",
                        route: exam
                            ? `/exams/AP/${examNameToUrlComponent(exam)}/score-calculator`
                            : `/exams/AP/score-calculator`,
                        alternateRoutes: ["/exams/AP/*/score-calculator", "/exams/AP/*/*/score-calculator"],
                    },
                ]}
                containerStyle={{
                    marginBottom: spacing.LG,
                }}
            />
            <SectionContainer
                title={sectionTitle}
                subtitle={sectionSubTitle}
                isPageHeader
                unusualKey={unusualKey}
                unusualAdditional={unusualAdditional}>
                <FlexColumnAlignJustifyCenter
                    className={styles.examSelectorInputs}
                    style={{
                        gap: spacing.MD,
                        paddingInline: spacing.LG,
                        width: "100%",
                    }}>
                    <Flex style={{ gap: spacing.SM, width: "100%", justifyContent: "center", alignItems: "flex-end" }}>
                        <SelectInput
                            label={"Which subject are you taking?"}
                            options={exams.map(exam => ({ value: exam, label: exam }))}
                            selected={exam ? { value: exam, label: exam } : undefined}
                            onChange={(value: string) => handleSelectSubject(value)}
                            placeholder={"Select subject"}
                            rootSx={{
                                backgroundColor: themeColors.neutralWhite,
                                border: "none",
                            }}
                            ulSx={{
                                backgroundColor: themeColors.neutralWhite,
                            }}
                        />
                        <CircularRectTextButton
                            onClick={() => {
                                handleStartStudy();
                            }}
                            sx={{
                                fontSize: "1.6rem",
                                width: "fit-content",
                                color: themeColors.primaryDark,
                                borderRadius: borderRadius.card,
                                backgroundColor: themeColors.primary,
                                padding: `1.3rem ${spacing.LG}`,
                            }}>
                            <span>{"Start Practicing!"}</span>
                        </CircularRectTextButton>
                        {isResponseRoom && (
                            <CircularRectTextButton
                                onClick={() => {
                                    if (!exam) {
                                        return toast.error("Please select an exam first.");
                                    }

                                    const element = document.getElementById("suggested-frq-list");
                                    element?.scrollIntoView({ behavior: "smooth" });
                                }}
                                sx={{
                                    fontSize: "1.6rem",
                                    width: "fit-content",
                                    color: themeColors.neutralBlack,
                                    borderRadius: borderRadius.card,
                                    backgroundColor: themeColors.neutralWhite,
                                    padding: `1.3rem ${spacing.LG}`,
                                }}>
                                <span>Or, see all prompts</span>
                            </CircularRectTextButton>
                        )}
                    </Flex>

                    <p className="secondaryText2" style={{ textAlign: "center", color: themeColors.neutral2 }}>
                        {"Knowt can make mistakes. Consider checking important information."}
                    </p>

                    <Image
                        src={practiceRoom}
                        alt="Pick your exam"
                        width={1000}
                        height={459}
                        priority
                        className={br.smDownDisplayNone}
                    />
                </FlexColumnAlignJustifyCenter>
            </SectionContainer>
        </>
    );
};

export default ExamSelectorSection;
